import { AlertDefaults, AlertSettings, AlertSocialMediaAccountDetails, AlertType, ChannelSettings, FacebookChannel, NextdoorAccount, NextdoorChannel, NextdoorChannelStatus, SocialApi, SocialMediaAccountModel, TwitterChannel } from "src/app/hickory-api";
import { AlertChannelsHelper, AlertFields, AlertModelService } from "..";
import { HtmlHelper } from "src/app/shared/html-helper/html-helper";
import { ApiConfig } from "src/app/shared/api-config/api-config";
import { SlicePipe } from "@angular/common";




export class AlertSocialHelper {

  static postProcess(service: AlertModelService, alert: AlertDefaults = service?.alertSettings, socialApi: SocialApi = service?.socialApi) {
    // don't re-fetch if we've already got accounts saved
    if (service?.socialAccounts) {
      this.deselectInvalidAccounts(alert, service.socialAccounts);
      return;
    }

    // some additional sanity checks
    if (!service || !alert || !socialApi) return;
    if (!alert?.fields?.find(f => f.name === AlertFields.SOCIAL)) return;
    if (!alert.channels?.facebook && !alert.channels?.twitter && !alert.channels?.nextdoor) return;

    // fetch social media accounts
    service.subscription.add(socialApi.getSocialMediaAccounts(parseInt(ApiConfig.tenantId), true).subscribe((result) => {
      service.state.socialAccounts = result;
      // by now the working copy of the alert on the service is probably not the same instance we're working with, so
      // apply the accounts response to the current working copy, if possible
      this.deselectInvalidAccounts(service?.alertSettings ?? alert, result);
      service.notifyFieldUpdated(AlertFields.SOCIAL);
    }));
  }
  
  static preparePreview(_service: AlertModelService, preview: AlertDefaults) {
    const channels = preview.channels;
    if (!channels) return;

    // send = true if accounts are selected; otherwise false
    if (channels.facebook) channels.facebook.send = !!channels.facebook.pageIds?.length;
    if (channels.twitter) channels.twitter.send = !!channels.twitter.accountIds?.length;
    if (channels.nextdoor) {
      channels.nextdoor.send = !!channels.nextdoor.accounts?.length;
      channels.nextdoor.accounts?.forEach(a => {
        // don't send empty groups array
        if (!a.groups?.length) a.groups = undefined;
      });
    }
  }

  static isSocialMediaOverLimit(service: AlertModelService, alert: AlertDefaults = service.alertSettings): boolean {
    if (alert?.alertType !== AlertType.Default) return false;
    if (!this.hasSelectedSocialMedia(alert)) return false
    const socialMessage = this.getSocialMediaMessage(alert);
    if (!socialMessage) return false;

    // check twitter
    if (alert?.channels?.twitter?.send) {
      const message = alert.channels.twitter.message || socialMessage;
      if (message.length > SocialChannel.TWITTER_LIMIT) return true;
    }

    // check nextdoor
    if (alert?.channels?.nextdoor?.send) {
      const message = alert.channels.nextdoor.message || socialMessage;
      if (message.length > SocialChannel.NEXTDOOR_LIMIT) return true;
    }

    return false;
  }

  static hasSelectedSocialMedia(alert: AlertDefaults): boolean {
    const channels = alert?.channels;
    if (!channels) return false;
    let valid = channels?.twitter?.send || channels?.facebook?.send || channels?.nextdoor?.send;
    if (channels?.twitter?.send) valid = valid && !!channels?.twitter?.accountIds?.length
    if (channels?.facebook?.send) valid = valid && !!channels?.facebook?.pageIds?.length
    if (channels?.nextdoor?.send) valid = valid && !!channels?.nextdoor?.accounts?.length
    return valid
  }

  static getSocialMediaMessage(alert: AlertDefaults): string {
    let message = HtmlHelper.convertHtmlToPlainText(alert?.message?.basic?.messageBody);
    if (!message) message = alert?.message?.basic?.message;
    return message;
  }

  static validateSocial(service: AlertModelService, channels: ChannelSettings = service.alertChannels): string[] {
    const errors = [];
    const facebook = channels?.facebook;
    const twitter = channels?.twitter;
    const nextdoor = channels?.nextdoor;

    // check at least one is selected
    if (!facebook?.send && !twitter?.send && !nextdoor?.send) {
      errors.push('FinishSocialMedia');
    }

    return errors;
  }

  static socialSummaryText(channels: ChannelSettings): string {
    let text = '';
    const facebook = channels?.facebook;
    if (facebook?.pageIds?.length > 0) {
      text += facebook.pageIds.length + ` ${AlertChannelsHelper.NAME_FACEBOOK} Page(s)`;
    }
    const twitter = channels?.twitter;
    if (twitter?.accountIds?.length > 0) {
      if (text.length) text += ', ';
      text += twitter.accountIds.length + ` ${AlertChannelsHelper.NAME_TWITTER} Account(s)`;
    }
    const nextdoor = channels?.nextdoor;
    if (nextdoor?.accounts?.length > 0) {
      if (text.length) text += ', ';
      text += nextdoor.accounts.length + ` ${AlertChannelsHelper.NAME_NEXTDOOR} Account(s)`;
    }

    return text.length ? text : null;
  }

  static accountDisplayName(account: SocialMediaAccountModel): string {
    const unknown = 'Unknown Account';
    switch (account?.SocialMediaAccountType) {
      case SocialEnum.Facebook: return account.SecretName ?? account.AccountName ?? unknown;
      case SocialEnum.Twitter: return `@${account.AccountName ?? unknown }`;
      case SocialEnum.Nextdoor: return account.AccountName ?? unknown;
    }

    return unknown;
  }

  static setAccountSelected(channel: SocialChannel, account: SocialMediaAccountModel, selected: boolean) {
    if (!channel || !account) return;

    let existingIds: Array<number>;
    switch (account.SocialMediaAccountType) {
      case SocialEnum.Facebook: existingIds = channel?.pageIds ?? new Array<number>(); break;
      case SocialEnum.Twitter: existingIds = channel?.accountIds ?? new Array<number>(); break;
      case SocialEnum.Nextdoor: existingIds = channel?.accounts?.map(a => a.id ?? -1) ?? new Array<number>(); break;
      default: return;
    }

    const index = existingIds.indexOf(account.Id);
    if (selected && index === -1) {
      existingIds.push(account.Id);
    } else if (!selected && index > -1) {
      existingIds.splice(index, 1);
    }

    channel.send = !!existingIds.length;
    switch (account.SocialMediaAccountType) {
      case SocialEnum.Facebook: channel.pageIds = existingIds; break;
      case SocialEnum.Twitter: channel.accountIds = existingIds; break;
      case SocialEnum.Nextdoor: {
        const index = channel.accounts.findIndex(a => a.id === account.Id);
        const converted: NextdoorAccount = channel.accounts[index] ?? { id: account.Id, name: account.AccountName, groups: [] };
        if (selected && index === -1) {
          channel.accounts.push(converted);
        } else if (!selected) {
          channel.accounts = channel.accounts.filter(a => a.id !== account.Id);
        }
        break;
      }
      default: return;
    }
  }

  static deselectInvalidAccounts(alert: AlertDefaults, accounts: SocialMediaAccountModel[]) {
    alert?.channels?.facebook?.pageIds?.forEach(id => {
      const channel = alert.channels.facebook;
      const found = accounts?.find(a => a.Id === id && a.SocialMediaAccountType === SocialEnum.Facebook);
      if (found?.IsAccessTokenValid !== true) {                       // if missing or expired
        channel.pageIds = channel.pageIds.filter(x => x !== id);      // deselect the account
        channel.send = !!channel.pageIds?.length;                     // only send if there are still accounts selected
      }
    });

    alert?.channels?.twitter?.accountIds?.forEach(id => {
      const channel = alert.channels.twitter;
      const found = accounts?.find(a => a.Id === id && a.SocialMediaAccountType === SocialEnum.Twitter);
      if (found?.IsAccessTokenValid !== true) {                        // if missing or expired
        channel.accountIds = channel.accountIds.filter(x => x !== id); // deselect the account
        channel.send = !!channel.accountIds?.length;                   // only send if there are still accounts selected
      }
    });

    alert?.channels?.nextdoor?.accounts?.forEach(account => {
      const channel = alert.channels.nextdoor;
      const found = accounts?.find(a => a.Id === account.id && a.SocialMediaAccountType === SocialEnum.Nextdoor);
      if (found?.IsAccessTokenValid !== true) {                        // if missing or expired
        channel.accounts = channel.accounts.filter(x => x !== account.id); // deselect the account
        channel.send = !!channel.accounts?.length;                     // only send if there are still accounts selected
      }
    });
  }
}

export type SocialEnum = SocialMediaAccountModel.SocialMediaAccountTypeEnum;
export const SocialEnum = {
  Facebook: 'Facebook' as SocialEnum,
  Twitter: 'Twitter' as SocialEnum,
  Nextdoor: 'Nextdoor' as SocialEnum,
};

// union of the social media alert channel types
export class SocialChannel implements FacebookChannel, TwitterChannel, NextdoorChannel {

  static readonly FACEBOOK_TYPE = SocialEnum.Facebook;
  static readonly TWITTER_TYPE = SocialEnum.Twitter;
  static readonly NEXTDOOR_TYPE = SocialEnum.Nextdoor;

  static readonly FACEBOOK_LIMIT = 0;
  static readonly TWITTER_LIMIT = 280;
  static readonly NEXTDOOR_LIMIT = 7800;

  socialType: SocialEnum
  recipientCount?: number;
  send?: boolean;
  message?: string;
  accountIds?: Array<number>; // Twitter
  pageIds?: Array<number>; // Facebook
  accounts?: Array<NextdoorAccount>; // Nextdoor
  hashTag?: string;

  static isFacebook(social: SocialChannel): boolean { return social?.socialType === SocialEnum.Facebook; }
  static isTwitter(social: SocialChannel): boolean { return social?.socialType === SocialEnum.Twitter; }
  static isNextdoor(social: SocialChannel): boolean { return social?.socialType === SocialEnum.Nextdoor; }

  static getSocialName(social: SocialChannel): string { return AlertChannelsHelper.getChannelName(social?.socialType?.toLowerCase()); }

  static fromFacebook(source: FacebookChannel): SocialChannel {
    if (!source) return null;
    const social = source as SocialChannel;
    social.socialType = SocialEnum.Facebook;
    return social;
  }

  static fromTwitter(source: TwitterChannel): SocialChannel {
    if (!source) return null;
    const social = source as SocialChannel;
    social.socialType = SocialEnum.Twitter;
    return social;
  }

  static fromNextdoor(source: NextdoorChannel): SocialChannel {
    if (!source) return null;
    const social = source as SocialChannel;
    social.socialType = SocialEnum.Nextdoor;
    return social;
  }
}

// partial union of AlertSocialMediaAccountDetails + NextdoorChannelStatus
// used in alert details views
interface SocialAccount {
  id?: number;
  type?: SocialEnum;
  name?: string;
  page?: string;
  postUrl?: string;
  isError?: boolean;
  statusCode?: string;
  statusMessages?: Array<string>;
  source?: object;
}

export class SocialPostDetails {
  type?: SocialEnum;
  message?: string;
  accounts?: Array<SocialAccount>;

  readonly truncateLimit = 450;
  isTruncated?: boolean = false;

  get platformName(): string { return this.type; }
  get componentId(): string { return this.type?.toLowerCase(); }
  get accountDisplayType(): string { return this.type === SocialEnum.Facebook ? 'Pages:' : 'Accounts:'; }
  get displayMessage(): string {
    return this.isTruncated ? new SlicePipe().transform(this.message, 0, this.truncateLimit) : this.message;
  }

  buildAccountName(account: SocialAccount): string {
    const prefix = account.type === SocialEnum.Twitter ? '@' : ''; // "@" for twitter prefix
    if (!account?.page?.length) return prefix + (account?.name ?? '');
    // we know we have a non-empty 'page' value at this point
    return account.name?.length ? `${prefix}${account.name} - ${account.page}` : `${prefix}${account.page}`;
  }

  buildStatusErrors(account: SocialAccount): string {
    const errors = new Array<string>;
    if (account?.isError !== true) return null;
    account.statusMessages?.forEach(m => {
      // the error messages should look like this:
      //     "Received an unexpected response from Nextdoor. Content: '{\"message\": \"some error from next door\"}'"
      // but we only want the value of the message property, so
      // parse it out if present, else return the whole string.
      // eslint-disable-next-line no-useless-escape
      const parts = m?.split(`Content: '{\"message\": \"`) ?? [''];
      // eslint-disable-next-line no-useless-escape
      errors.push(parts.length === 2 ? parts[1].replace(`\"}'`, '') : parts[0]);
    });

    return errors.length ? errors.join(', ') : null;
  }

  static fromFacebook(alert: AlertSettings): SocialPostDetails {
    const source = SocialChannel.fromFacebook(alert?.channels?.facebook);
    if (!source?.send) return null;
    return this.fromChannel(source, AlertSocialHelper.getSocialMediaMessage(alert));
  }

  static fromTwitter(alert: AlertSettings): SocialPostDetails {
    const source = SocialChannel.fromTwitter(alert?.channels?.twitter);
    if (!source?.send) return null;
    return this.fromChannel(source, AlertSocialHelper.getSocialMediaMessage(alert));
  }

  static fromNextdoor(alert: AlertSettings): SocialPostDetails {
    const source = SocialChannel.fromNextdoor(alert?.channels?.nextdoor);
    if (!source?.send) return null;
    return this.fromChannel(source, AlertSocialHelper.getSocialMediaMessage(alert));
  }
  
  static fromChannel(source: SocialChannel, alertMessage: string): SocialPostDetails {
    if (!source) return null;
    const details = new SocialPostDetails();
    details.type = source.socialType;
    details.updateMessage(source.message || alertMessage);
    details.accounts = [];
    const accountIds = source.pageIds ?? source.accountIds ?? source.accounts?.map(it => it.id);
    accountIds?.forEach(id => {
      const account: SocialAccount = {
        id: id,
        type: source.socialType,
        name: source.accounts?.find(it => it.id === id)?.name,
        source: source,
      };
      details.accounts.push(account);
    });

    return details;
  }

  updateMessage(message: string) {
    this.message = message;
    this.isTruncated = (this.message?.length ?? 0) > this.truncateLimit;
  }

  applyDetails(sources: Array<AlertSocialMediaAccountDetails>) {
    const first = sources?.find(_ => true);
    // early exit if sources is empty or null or wrong type
    if (first?.type !== this.type) return;

    // update the message if we get one here
    if (first.message) this.updateMessage(first.message);

    // best we can do is assume that the list of sources matches up to our list of accounts
    // since these source models don't have an ID property
    const existingAccounts: Array<SocialAccount> = this.accounts ?? [];
    this.accounts = existingAccounts;
    sources?.forEach((source, index) => {
      const account = existingAccounts[index] ?? { id: -1, };
      account.type = source.type;
      account.name = source.accountName;
      account.page = source.pageName;
      account.source = source;
      existingAccounts[index] = account;
    });
  }

  applyStatus(sources: Array<NextdoorChannelStatus>) {
    const first = sources?.find(_ => true);
     // early exit if sources is empty or null or wrong type
    if (!first || this.type !== SocialEnum.Nextdoor) return;

    // the Nextdoor accounts in these source have more info, so we
    // shouldn't need to guess when matching things up
    const existingAccounts: Array<SocialAccount> = this.accounts ?? [];
    this.accounts = existingAccounts;
    sources?.forEach((source, index) => {
      const account = existingAccounts.find(it => it.id === source.accountId) ?? { type: SocialEnum.Nextdoor, };
      account.id = source.accountId;
      account.name = source.accountName;
      account.postUrl = source.postUrl;
      account.isError = source.isError;
      account.statusCode = source.statusCode;
      account.statusMessages = source.messages;
      account.source = source;
      existingAccounts[index] = account;
    });
  }
}
